'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
  PhotoIcon, StarIcon, DocumentTextIcon, CheckCircleIcon, ArrowLeftIcon,
  LockClosedIcon, ClockIcon, CloudArrowUpIcon, XMarkIcon, EyeIcon,
  CurrencyDollarIcon, WalletIcon, CameraIcon, GlobeAltIcon,
  RectangleGroupIcon, NewspaperIcon, MegaphoneIcon, Squares2X2Icon,
  HomeIcon, MagnifyingGlassIcon, UserGroupIcon, ShieldExclamationIcon,
  ClipboardDocumentListIcon, ChartBarIcon, AcademicCapIcon, MapPinIcon,
} from '@heroicons/react/24/outline';
import { createClient } from '@/lib/local-api/client';
import { toast } from 'sonner';

type AdType = 'banner' | 'featured_listing' | 'sponsored_post' | 'mini_card' | 'side_strip' | 'flash_banner';
type PlacementSlot =
  | 'home_banner' | 'home_mini_top' | 'home_mini_mid' | 'home_side_strip' |'explore_banner'| 'explore_mini' |'community_feed'| 'community_sidebar' |'scam_alerts_sidebar'| 'scam_alerts_banner' |'project_reviews_header'| 'project_ratings_top' |'learning_hub_sidebar' | 'learning_hub_banner';
type AdModel = 'paid' | 'free' | 'free_exchange';

interface AdTypeOption {
  id: AdType;
  name: string;
  icon: React.ElementType;
  dailyRate: number;
  impressions: string;
  color: string;
  bgColor: string;
  borderColor: string;
  size: string;
  features: string[];
}

interface PlacementSlotOption {
  id: PlacementSlot;
  label: string;
  page: string;
  pageIcon: React.ElementType;
  size: string;
  visibility: 'High' | 'Medium' | 'Low';
  visibilityColor: string;
  description: string;
  compatibleTypes: AdType[];
}

interface AdTypeCategory {
  label: string;
  icon: React.ElementType;
  description: string;
  types: AdType[];
}

interface PlacementCategory {
  label: string;
  icon: React.ElementType;
  color: string;
  slots: PlacementSlot[];
}

const defaultDailyRates: Record<AdType, number> = {
  banner: 10,
  featured_listing: 7,
  sponsored_post: 4,
  mini_card: 3,
  side_strip: 5,
  flash_banner: 8,
};

const adTypeCategories: AdTypeCategory[] = [
  {
    label: 'Display Ads',
    icon: RectangleGroupIcon,
    description: 'Visual banner and strip formats — maximum visibility',
    types: ['banner', 'flash_banner', 'side_strip'],
  },
  {
    label: 'Content Ads',
    icon: NewspaperIcon,
    description: 'Blend into content feeds and listings — high engagement',
    types: ['featured_listing', 'sponsored_post', 'mini_card'],
  },
];

const placementCategories: PlacementCategory[] = [
  {
    label: 'High Traffic Pages',
    icon: MegaphoneIcon,
    color: 'text-amber-500',
    slots: ['home_banner', 'home_mini_top', 'home_mini_mid', 'home_side_strip', 'explore_banner', 'explore_mini'],
  },
  {
    label: 'Community & Social',
    icon: UserGroupIcon,
    color: 'text-cyan-400',
    slots: ['community_feed', 'community_sidebar'],
  },
  {
    label: 'Project Pages',
    icon: ClipboardDocumentListIcon,
    color: 'text-green-400',
    slots: ['project_reviews_header', 'project_ratings_top'],
  },
  {
    label: 'Niche & Sidebar Slots',
    icon: Squares2X2Icon,
    color: 'text-purple-400',
    slots: ['scam_alerts_sidebar', 'scam_alerts_banner', 'learning_hub_sidebar', 'learning_hub_banner'],
  },
];

const allAdTypes: AdTypeOption[] = [
  {
    id: 'banner',
    name: 'Sponsored Banner',
    icon: PhotoIcon,
    dailyRate: defaultDailyRates.banner,
    impressions: '40,000–80,000',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    size: '1200 × 300 px',
    features: ['Full-width banner', 'High-traffic pages', 'Custom headline & CTA', 'Click-through tracking'],
  },
  {
    id: 'flash_banner',
    name: 'Flash Banner',
    icon: MegaphoneIcon,
    dailyRate: defaultDailyRates.flash_banner,
    impressions: '35,000–70,000',
    color: 'text-orange-400',
    bgColor: 'bg-orange-400/10',
    borderColor: 'border-orange-400/30',
    size: '1200 × 120 px',
    features: ['Slim top-of-page strip', 'Auto-dismissible', 'Urgent / promo messaging', 'High click rate'],
  },
  {
    id: 'side_strip',
    name: 'Side Strip Ad',
    icon: RectangleGroupIcon,
    dailyRate: defaultDailyRates.side_strip,
    impressions: '25,000–50,000',
    color: 'text-pink-400',
    bgColor: 'bg-pink-400/10',
    borderColor: 'border-pink-400/30',
    size: '160 × 600 px',
    features: ['Sticky sidebar strip', 'Visible while scrolling', 'Desktop & tablet', 'Brand awareness'],
  },
  {
    id: 'featured_listing',
    name: 'Featured Listing',
    icon: StarIcon,
    dailyRate: defaultDailyRates.featured_listing,
    impressions: '20,000–45,000',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-400/10',
    borderColor: 'border-cyan-400/30',
    size: '800 × 600 px',
    features: ['Pinned to top of lists', '"Featured" badge', 'Priority in search', 'Enhanced project card'],
  },
  {
    id: 'sponsored_post',
    name: 'Sponsored Post',
    icon: DocumentTextIcon,
    dailyRate: defaultDailyRates.sponsored_post,
    impressions: '10,000–25,000',
    color: 'text-green-400',
    bgColor: 'bg-green-400/10',
    borderColor: 'border-green-400/30',
    size: '600 × 400 px',
    features: ['In community feed', 'Clearly marked Sponsored', 'Rich text description', 'Audience targeting'],
  },
  {
    id: 'mini_card',
    name: 'Mini Card Ad',
    icon: Squares2X2Icon,
    dailyRate: defaultDailyRates.mini_card,
    impressions: '8,000–18,000',
    color: 'text-violet-400',
    bgColor: 'bg-violet-400/10',
    borderColor: 'border-violet-400/30',
    size: '300 × 200 px',
    features: ['Compact inline card', 'Between content sections', 'Mobile-friendly', 'Cost-effective'],
  },
];

const allPlacementSlots: PlacementSlotOption[] = [
  // High Traffic
  { id: 'home_banner', label: 'Home — Main Banner', page: 'Home', pageIcon: HomeIcon, size: '1200×300', visibility: 'High', visibilityColor: 'text-green-400', description: 'Top-of-page banner, first thing visitors see', compatibleTypes: ['banner', 'flash_banner'] },
  { id: 'home_mini_top', label: 'Home — Mini Top Strip', page: 'Home', pageIcon: HomeIcon, size: '300×200', visibility: 'High', visibilityColor: 'text-green-400', description: 'Pill/card ad between hero and content', compatibleTypes: ['mini_card', 'sponsored_post'] },
  { id: 'home_mini_mid', label: 'Home — Mid-Page Mini', page: 'Home', pageIcon: HomeIcon, size: '300×200', visibility: 'Medium', visibilityColor: 'text-amber-400', description: 'Inline mini card between content sections', compatibleTypes: ['mini_card', 'sponsored_post'] },
  { id: 'home_side_strip', label: 'Home — Side Strip', page: 'Home', pageIcon: HomeIcon, size: '160×600', visibility: 'High', visibilityColor: 'text-green-400', description: 'Sticky side strip visible while scrolling', compatibleTypes: ['side_strip'] },
  { id: 'explore_banner', label: 'Explore — Page Banner', page: 'Explore', pageIcon: MagnifyingGlassIcon, size: '1200×300', visibility: 'High', visibilityColor: 'text-green-400', description: 'Banner at top of the Explore/discovery page', compatibleTypes: ['banner', 'flash_banner'] },
  { id: 'explore_mini', label: 'Explore — Mini Card', page: 'Explore', pageIcon: MagnifyingGlassIcon, size: '300×200', visibility: 'Medium', visibilityColor: 'text-amber-400', description: 'Compact card within project listings', compatibleTypes: ['mini_card', 'featured_listing'] },
  // Community
  { id: 'community_feed', label: 'Community — Feed Post', page: 'Community', pageIcon: UserGroupIcon, size: '600×400', visibility: 'High', visibilityColor: 'text-green-400', description: 'Appears as a sponsored post in the social feed', compatibleTypes: ['sponsored_post', 'featured_listing'] },
  { id: 'community_sidebar', label: 'Community — Sidebar', page: 'Community', pageIcon: UserGroupIcon, size: '300×200', visibility: 'Medium', visibilityColor: 'text-amber-400', description: 'Mini card in the community sidebar panel', compatibleTypes: ['mini_card', 'side_strip'] },
  // Project Pages
  { id: 'project_reviews_header', label: 'Project Reviews — Header', page: 'Reviews', pageIcon: ClipboardDocumentListIcon, size: '1200×300', visibility: 'Medium', visibilityColor: 'text-amber-400', description: 'Banner above the project reviews list', compatibleTypes: ['banner', 'featured_listing'] },
  { id: 'project_ratings_top', label: 'Project Ratings — Top', page: 'Ratings', pageIcon: ChartBarIcon, size: '1200×300', visibility: 'Medium', visibilityColor: 'text-amber-400', description: 'Top placement on the ratings comparison page', compatibleTypes: ['banner', 'featured_listing'] },
  // Niche & Sidebar
  { id: 'scam_alerts_sidebar', label: 'Scam Alerts — Sidebar', page: 'Scam Alerts', pageIcon: ShieldExclamationIcon, size: '160×600', visibility: 'Medium', visibilityColor: 'text-amber-400', description: 'Sidebar strip on the scam alert center', compatibleTypes: ['side_strip', 'mini_card'] },
  { id: 'scam_alerts_banner', label: 'Scam Alerts — Banner', page: 'Scam Alerts', pageIcon: ShieldExclamationIcon, size: '1200×300', visibility: 'Low', visibilityColor: 'text-rose-400', description: 'Banner at top of scam alert center page', compatibleTypes: ['banner'] },
  { id: 'learning_hub_sidebar', label: 'Learning Hub — Sidebar', page: 'Learning', pageIcon: AcademicCapIcon, size: '160×600', visibility: 'Medium', visibilityColor: 'text-amber-400', description: 'Sidebar strip on the learning hub page', compatibleTypes: ['side_strip', 'mini_card'] },
  { id: 'learning_hub_banner', label: 'Learning Hub — Banner', page: 'Learning', pageIcon: AcademicCapIcon, size: '1200×300', visibility: 'Low', visibilityColor: 'text-rose-400', description: 'Banner at top of the learning hub page', compatibleTypes: ['banner', 'featured_listing'] },
];

const categories = ['Crypto', 'Forex', 'DeFi', 'Investment', 'AI Project', 'Online Earning', 'Business'];

// ─── Ad Preview Component ────────────────────────────────────────────────────
interface AdPreviewProps {
  adType: AdType | null;
  headline: string;
  description: string;
  ctaText: string;
  imagePreview: string | null;
  projectName: string;
  placementLabel: string;
}

function AdPreview({ adType, headline, description, ctaText, imagePreview, projectName, placementLabel }: AdPreviewProps) {
  const displayHeadline = headline || 'Your Ad Headline Here';
  const displayDesc = description || 'Your ad description will appear here. Make it compelling to attract clicks.';
  const displayCta = ctaText || 'Learn More';
  const displayProject = projectName || 'Your Project';

  if (adType === 'banner' || adType === 'flash_banner') {
    const accentColor = adType === 'flash_banner' ? 'amber-400' : 'amber-500';
    return (
      <div className={`rounded-xl overflow-hidden border border-${accentColor}/30 bg-gradient-to-r from-${accentColor}/10 via-card to-${accentColor}/5 shadow-sm`}>
        <div className="flex items-stretch gap-0 min-h-[90px]">
          {imagePreview ? (
            <div className="w-28 flex-shrink-0">
              <img src={imagePreview} alt="Ad preview" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className={`w-28 flex-shrink-0 bg-${accentColor}/10 flex items-center justify-center`}>
              <PhotoIcon style={{ width: 28, height: 28 }} className={`text-${accentColor}/40`} />
            </div>
          )}
          <div className="flex-1 px-4 py-3 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded bg-${accentColor}/20 text-${accentColor} uppercase tracking-wider`}>
                  {adType === 'flash_banner' ? 'Flash' : 'Sponsored'}
                </span>
                <span className="text-[10px] text-muted-foreground">{placementLabel}</span>
              </div>
              <p className="text-sm font-bold text-foreground leading-tight line-clamp-1">{displayHeadline}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{displayDesc}</p>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] text-muted-foreground font-medium">{displayProject}</span>
              <button className={`text-[10px] font-bold px-3 py-1 rounded-full bg-${accentColor} text-white hover:opacity-90 transition-opacity`}>
                {displayCta}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (adType === 'side_strip') {
    return (
      <div className="rounded-xl border border-pink-400/30 bg-card shadow-sm overflow-hidden max-w-[160px]">
        {imagePreview ? (
          <img src={imagePreview} alt="Ad preview" className="w-full h-24 object-cover" />
        ) : (
          <div className="w-full h-24 bg-gradient-to-b from-pink-400/10 to-pink-400/5 flex items-center justify-center">
            <RectangleGroupIcon style={{ width: 24, height: 24 }} className="text-pink-400/40" />
          </div>
        )}
        <div className="p-2">
          <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-pink-400/20 text-pink-400 uppercase tracking-wider">Side Ad</span>
          <p className="text-[11px] font-bold text-foreground mt-1 line-clamp-2">{displayHeadline}</p>
          <button className="mt-1.5 w-full text-[9px] font-bold py-1 rounded-full bg-pink-400 text-white">{displayCta}</button>
        </div>
      </div>
    );
  }

  if (adType === 'featured_listing') {
    return (
      <div className="rounded-xl border border-cyan-400/30 bg-card shadow-sm overflow-hidden">
        <div className="relative">
          {imagePreview ? (
            <img src={imagePreview} alt="Ad preview" className="w-full h-32 object-cover" />
          ) : (
            <div className="w-full h-32 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 flex items-center justify-center">
              <StarIcon style={{ width: 32, height: 32 }} className="text-cyan-400/30" />
            </div>
          )}
          <div className="absolute top-2 left-2 flex items-center gap-1">
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-cyan-400/90 text-background uppercase tracking-wider">Featured</span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-background/80 text-muted-foreground uppercase tracking-wider">Sponsored</span>
          </div>
        </div>
        <div className="p-3">
          <p className="text-sm font-bold text-foreground leading-tight line-clamp-1">{displayHeadline}</p>
          <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{displayDesc}</p>
          <div className="flex items-center justify-between mt-2.5">
            <span className="text-[10px] text-muted-foreground font-medium">{displayProject}</span>
            <button className="text-[10px] font-bold px-3 py-1 rounded-full bg-cyan-400 text-background hover:opacity-90 transition-opacity">{displayCta}</button>
          </div>
        </div>
      </div>
    );
  }

  if (adType === 'sponsored_post') {
    return (
      <div className="rounded-xl border border-green-400/30 bg-card shadow-sm p-3.5">
        <div className="flex items-start gap-3">
          {imagePreview ? (
            <img src={imagePreview} alt="Ad preview" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
              <DocumentTextIcon style={{ width: 20, height: 20 }} className="text-green-400/50" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-400/20 text-green-400 uppercase tracking-wider">Sponsored Post</span>
            </div>
            <p className="text-sm font-bold text-foreground leading-tight line-clamp-1">{displayHeadline}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{displayDesc}</p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-border">
          <span className="text-[10px] text-muted-foreground font-medium">{displayProject}</span>
          <button className="text-[10px] font-bold px-3 py-1 rounded-full bg-green-400 text-background hover:opacity-90 transition-opacity">{displayCta}</button>
        </div>
      </div>
    );
  }

  if (adType === 'mini_card') {
    return (
      <div className="rounded-xl border border-violet-400/30 bg-card shadow-sm p-3 max-w-[200px]">
        <div className="flex items-center gap-2 mb-2">
          {imagePreview ? (
            <img src={imagePreview} alt="Ad preview" className="w-8 h-8 rounded object-cover flex-shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded bg-violet-400/10 flex items-center justify-center flex-shrink-0">
              <Squares2X2Icon style={{ width: 14, height: 14 }} className="text-violet-400/50" />
            </div>
          )}
          <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-violet-400/20 text-violet-400 uppercase tracking-wider">Mini Ad</span>
        </div>
        <p className="text-[11px] font-bold text-foreground line-clamp-2">{displayHeadline}</p>
        <button className="mt-2 w-full text-[9px] font-bold py-1 rounded-full bg-violet-400 text-white">{displayCta}</button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-dashed border-border bg-muted/20 flex items-center justify-center h-24">
      <p className="text-xs text-muted-foreground">Select an ad type to see preview</p>
    </div>
  );
}

const pageColors: Record<string, string> = {
  Home: '#f59e0b',
  Explore: '#06b6d4',
  Community: '#8b5cf6',
  Reviews: '#10b981',
  Ratings: '#10b981',
  'Scam Alerts': '#ef4444',
  Learning: '#3b82f6',
};

// ─── Ad Placement Info Panel ─────────────────────────────────────────────────
interface AdPlacementInfoProps {
  slotData: PlacementSlotOption;
  adTypeData: AdTypeOption;
}

const pageRoutes: Record<string, string> = {
  Home: '/home',
  Explore: '/explore',
  Community: '/community-network',
  Reviews: '/project-reviews',
  Ratings: '/project-ratings',
  'Scam Alerts': '/scam-alert-center',
  Learning: '/learning-hub',
};

const slotPositionLabels: Record<PlacementSlot, { section: string; position: string; context: string }> = {
  home_banner:            { section: 'Hero Section', position: 'Top of page — full width', context: 'First thing every visitor sees when landing on Home' },
  home_mini_top:          { section: 'Content Area', position: 'Upper section — left column', context: 'Between the hero and main content blocks' },
  home_mini_mid:          { section: 'Content Area', position: 'Mid-page — left column', context: 'Inline between content sections, high scroll depth' },
  home_side_strip:        { section: 'Sidebar', position: 'Right sidebar — sticky', context: 'Stays visible as users scroll through the entire page' },
  explore_banner:         { section: 'Page Header', position: 'Top of page — full width', context: 'Above all project listings on the Explore page' },
  explore_mini:           { section: 'Listings Area', position: 'Mid-page — inline card', context: 'Embedded within the project discovery grid' },
  community_feed:         { section: 'Social Feed', position: 'In-feed — top & every 5 posts', context: 'Appears as a sponsored post in the community feed' },
  community_sidebar:      { section: 'Right Sidebar', position: 'Sidebar panel — upper', context: 'Visible to all users browsing the community page' },
  project_reviews_header: { section: 'Page Header', position: 'Above reviews list — full width', context: 'Seen by users researching project credibility' },
  project_ratings_top:    { section: 'Page Header', position: 'Top of ratings page — full width', context: 'Seen by users comparing project ratings' },
  scam_alerts_sidebar:    { section: 'Sidebar', position: 'Right sidebar — sticky strip', context: 'Visible to users checking scam reports' },
  scam_alerts_banner:     { section: 'Page Header', position: 'Top of page — full width', context: 'Above all scam alert listings' },
  learning_hub_sidebar:   { section: 'Sidebar', position: 'Right sidebar — sticky strip', context: 'Visible to users browsing courses and tutorials' },
  learning_hub_banner:    { section: 'Page Header', position: 'Top of page — full width', context: 'Above all learning content on the hub' },
};

function AdPlacementInfoPanel({ slotData, adTypeData }: AdPlacementInfoProps) {
  const pageColor = pageColors[slotData.page] ?? '#6366f1';
  const route = pageRoutes[slotData.page] ?? '/';
  const posInfo = slotPositionLabels[slotData.id];
  const PageIcon = slotData.pageIcon;
  const TypeIcon = adTypeData.icon;

  return (
    <div
      className="rounded-xl border p-4 space-y-3"
      style={{ borderColor: `${pageColor}40`, backgroundColor: `${pageColor}08` }}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${pageColor}20` }}>
          <MapPinIcon style={{ width: 14, height: 14, color: pageColor }} />
        </div>
        <span className="text-xs font-bold text-foreground uppercase tracking-wider">Where Your Ad Will Appear</span>
      </div>

      {/* Page + Route */}
      <div className="flex items-center gap-3 p-3 bg-background/60 rounded-xl border border-border">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${pageColor}18` }}>
          <PageIcon style={{ width: 16, height: 16, color: pageColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-foreground">{slotData.page} Page</p>
          <p className="text-[11px] font-mono text-muted-foreground">bizzbox.com{route}</p>
        </div>
        <span
          className="text-[9px] font-bold px-2 py-1 rounded-full"
          style={{ backgroundColor: `${pageColor}20`, color: pageColor }}
        >
          {slotData.visibility} Traffic
        </span>
      </div>

      {/* Placement Details Grid */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2.5 bg-background/60 rounded-lg border border-border">
          <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Section</p>
          <p className="text-xs font-semibold text-foreground">{posInfo.section}</p>
        </div>
        <div className="p-2.5 bg-background/60 rounded-lg border border-border">
          <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Ad Format</p>
          <div className="flex items-center gap-1.5">
            <TypeIcon style={{ width: 11, height: 11 }} className={adTypeData.color} />
            <p className="text-xs font-semibold text-foreground">{adTypeData.name}</p>
          </div>
        </div>
        <div className="col-span-2 p-2.5 bg-background/60 rounded-lg border border-border">
          <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Exact Position</p>
          <p className="text-xs font-semibold text-foreground">{posInfo.position}</p>
        </div>
        <div className="col-span-2 p-2.5 rounded-lg" style={{ backgroundColor: `${pageColor}10`, border: `1px solid ${pageColor}25` }}>
          <p className="text-[9px] font-semibold uppercase tracking-wider mb-1" style={{ color: pageColor }}>Audience Context</p>
          <p className="text-xs text-muted-foreground">{posInfo.context}</p>
        </div>
      </div>

      {/* Size + Visibility bar */}
      <div className="flex items-center gap-3">
        <span className="text-[10px] font-mono bg-background/80 border border-border px-2 py-1 rounded text-muted-foreground">{slotData.size} px</span>
        <div className="flex-1 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                backgroundColor: pageColor,
                width: slotData.visibility === 'High' ? '85%' : slotData.visibility === 'Medium' ? '55%' : '30%',
              }}
            />
          </div>
          <span className={`text-[10px] font-semibold ${slotData.visibilityColor}`}>{slotData.visibility} Visibility</span>
        </div>
      </div>
    </div>
  );
}

// ─── Page Layout Visualizer ──────────────────────────────────────────────────
interface PageLayoutProps {
  page: string;
  highlightSlot: PlacementSlot | null;
  hoveredSlot: PlacementSlot | null;
}

const slotPositions: Record<PlacementSlot, { page: string; zone: string; style: React.CSSProperties; label: string }> = {
  home_banner:            { page: 'Home',      zone: 'top',     style: { top: '8%',  left: '5%',  right: '5%',  height: '12%' }, label: 'Main Banner' },
  home_mini_top:          { page: 'Home',      zone: 'upper',   style: { top: '24%', left: '5%',  width: '42%', height: '10%' }, label: 'Mini Top' },
  home_mini_mid:          { page: 'Home',      zone: 'mid',     style: { top: '50%', left: '5%',  width: '42%', height: '10%' }, label: 'Mid Mini' },
  home_side_strip:        { page: 'Home',      zone: 'side',    style: { top: '22%', right: '2%', width: '14%', height: '60%' }, label: 'Side Strip' },
  explore_banner:         { page: 'Explore',   zone: 'top',     style: { top: '8%',  left: '5%',  right: '5%',  height: '12%' }, label: 'Page Banner' },
  explore_mini:           { page: 'Explore',   zone: 'mid',     style: { top: '40%', left: '5%',  width: '42%', height: '10%' }, label: 'Mini Card' },
  community_feed:         { page: 'Community', zone: 'feed',    style: { top: '30%', left: '5%',  width: '58%', height: '14%' }, label: 'Feed Post' },
  community_sidebar:      { page: 'Community', zone: 'sidebar', style: { top: '22%', right: '2%', width: '28%', height: '20%' }, label: 'Sidebar' },
  project_reviews_header: { page: 'Reviews',   zone: 'top',     style: { top: '8%',  left: '5%',  right: '5%',  height: '12%' }, label: 'Header Banner' },
  project_ratings_top:    { page: 'Ratings',   zone: 'top',     style: { top: '8%',  left: '5%',  right: '5%',  height: '12%' }, label: 'Top Banner' },
  scam_alerts_sidebar:    { page: 'Scam Alerts', zone: 'sidebar', style: { top: '22%', right: '2%', width: '14%', height: '55%' }, label: 'Sidebar Strip' },
  scam_alerts_banner:     { page: 'Scam Alerts', zone: 'top',   style: { top: '8%',  left: '5%',  right: '5%',  height: '12%' }, label: 'Top Banner' },
  learning_hub_sidebar:   { page: 'Learning',  zone: 'sidebar', style: { top: '22%', right: '2%', width: '14%', height: '55%' }, label: 'Sidebar Strip' },
  learning_hub_banner:    { page: 'Learning',  zone: 'top',     style: { top: '8%',  left: '5%',  right: '5%',  height: '12%' }, label: 'Top Banner' },
};

function PageLayoutVisualizer({ page, highlightSlot, hoveredSlot }: PageLayoutProps) {
  const activeSlot = hoveredSlot ?? highlightSlot;
  const pageColor = pageColors[page] ?? '#6366f1';
  const slotsOnPage = Object.entries(slotPositions).filter(([, v]) => v.page === page);

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-border bg-background" style={{ paddingBottom: '75%' }}>
      <div className="absolute inset-0 flex flex-col">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-muted border-b border-border flex-shrink-0">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-400/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
          <div className="flex-1 mx-2 bg-background/60 rounded-md px-2 py-0.5 text-[9px] text-muted-foreground font-mono truncate">
            bizzbox.com/{page.toLowerCase().replace(' ', '-')}
          </div>
        </div>

        {/* Page body */}
        <div className="flex-1 relative overflow-hidden">
          {/* Nav bar */}
          <div className="absolute top-0 left-0 right-0 h-[7%] bg-card/80 border-b border-border/50 flex items-center px-3 gap-2 z-10">
            <div className="w-12 h-2 rounded bg-primary/30" />
            <div className="flex-1" />
            <div className="w-6 h-2 rounded bg-muted-foreground/20" />
            <div className="w-6 h-2 rounded bg-muted-foreground/20" />
            <div className="w-6 h-2 rounded bg-muted-foreground/20" />
          </div>

          {/* Content skeleton */}
          <div className="absolute inset-0 pt-[7%]">
            {/* Page title */}
            <div className="absolute top-[10%] left-[5%] w-32 h-2.5 rounded bg-muted-foreground/15" />
            {/* Content blocks */}
            {[28, 40, 52, 64, 76].map((top) => (
              <div key={top} className="absolute left-[5%] rounded bg-muted/40" style={{ top: `${top}%`, width: '55%', height: '6%' }} />
            ))}
            {/* Sidebar skeleton */}
            {[28, 50, 72].map((top) => (
              <div key={top} className="absolute right-[18%] rounded bg-muted/30" style={{ top: `${top}%`, width: '22%', height: '8%' }} />
            ))}
          </div>

          {/* Ad slot overlays */}
          {slotsOnPage.map(([slotId, pos]) => {
            const isActive = activeSlot === slotId;
            const isHighlighted = highlightSlot === slotId;
            return (
              <div
                key={slotId}
                className="absolute rounded transition-all duration-300 flex items-center justify-center"
                style={{
                  ...pos.style,
                  backgroundColor: isActive ? `${pageColor}30` : `${pageColor}10`,
                  border: `${isActive ? 2 : 1}px ${isActive ? 'solid' : 'dashed'} ${pageColor}${isActive ? 'cc' : '50'}`,
                  zIndex: isActive ? 20 : 10,
                  boxShadow: isActive ? `0 0 12px ${pageColor}40` : 'none',
                }}
              >
                <span
                  className="text-[7px] font-bold text-center leading-tight px-1"
                  style={{ color: pageColor, opacity: isActive ? 1 : 0.6 }}
                >
                  {isActive ? `📍 ${pos.label}` : pos.label}
                </span>
              </div>
            );
          })}

          {/* Page label */}
          <div
            className="absolute bottom-2 right-2 text-[8px] font-bold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${pageColor}20`, color: pageColor }}
          >
            {page} Page
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Slot Card ───────────────────────────────────────────────────────────────
interface SlotCardProps {
  slot: PlacementSlotOption;
  isSelected: boolean;
  isCompatible: boolean;
  isHovered: boolean;
  onSelect: () => void;
  onHover: (id: PlacementSlot | null) => void;
}

function SlotCard({ slot, isSelected, isCompatible, isHovered, onSelect, onHover }: SlotCardProps) {
  const SlotIcon = slot.pageIcon;
  const pageColor = pageColors[slot.page] ?? '#6366f1';

  return (
    <button
      onClick={() => isCompatible && onSelect()}
      onMouseEnter={() => isCompatible && onHover(slot.id)}
      onMouseLeave={() => onHover(null)}
      disabled={!isCompatible}
      className={`w-full text-left rounded-xl border transition-all duration-200 overflow-hidden ${
        isSelected
          ? 'border-primary bg-primary/5 shadow-sm'
          : isCompatible
          ? 'border-border bg-card hover:border-primary/40 hover:bg-card/80' :'border-border bg-muted/20 opacity-40 cursor-not-allowed'
      }`}
    >
      <div className="flex items-start gap-3 p-3">
        {/* Page color indicator */}
        <div
          className="w-1 self-stretch rounded-full flex-shrink-0"
          style={{ backgroundColor: isCompatible ? pageColor : 'transparent', minHeight: 36 }}
        />

        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: isCompatible ? `${pageColor}18` : undefined }}
        >
          <SlotIcon
            style={{ width: 15, height: 15, color: isCompatible ? pageColor : undefined }}
            className={!isCompatible ? 'text-muted-foreground' : ''}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <p className="text-sm font-semibold text-foreground leading-tight">{slot.label}</p>
            {isCompatible && (
              <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-400 flex-shrink-0">✓ Compatible</span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground line-clamp-1 mb-1.5">{slot.description}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{slot.size}</span>
            <span className={`text-[9px] font-semibold ${slot.visibilityColor}`}>● {slot.visibility} Visibility</span>
            <span
              className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: `${pageColor}15`, color: pageColor }}
            >
              {slot.page}
            </span>
          </div>
        </div>

        {isSelected && (
          <CheckCircleIcon className="text-primary flex-shrink-0 mt-0.5" style={{ width: 16, height: 16 }} />
        )}
      </div>

      {/* Visual position hint bar */}
      {isCompatible && (
        <div
          className="px-3 pb-2.5 flex items-center gap-1.5"
        >
          <MapPinIcon style={{ width: 10, height: 10 }} className="text-muted-foreground flex-shrink-0" />
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                backgroundColor: pageColor,
                width: slot.visibility === 'High' ? '85%' : slot.visibility === 'Medium' ? '55%' : '30%',
                opacity: isSelected || isHovered ? 1 : 0.4,
              }}
            />
          </div>
          <span className="text-[9px] text-muted-foreground flex-shrink-0">
            {slot.visibility === 'High' ? 'Prime spot' : slot.visibility === 'Medium' ? 'Good reach' : 'Niche audience'}
          </span>
        </div>
      )}
    </button>
  );
}

export default function PlaceAdContent() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // ── Derive initial locked state directly from URL params (no useEffect delay) ──
  const initialSlotParam = searchParams.get('slot') as PlacementSlot | null;
  const initialSlotData = initialSlotParam ? allPlacementSlots.find(s => s.id === initialSlotParam) ?? null : null;
  const initialIsLocked = !!initialSlotData;
  const initialSelectedSlot: PlacementSlot | null = initialSlotData ? initialSlotParam : null;
  const initialSelectedType: AdType | null = initialSlotData ? (initialSlotData.compatibleTypes[0] ?? null) : null;
  const initialPreselectedPage: string | null = initialSlotData ? initialSlotData.page : null;

  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(initialIsLocked ? 1 : 1);
  const [user, setUser] = useState<any>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [preselectedFromPage, setPreselectedFromPage] = useState<string | null>(initialPreselectedPage);
  const [isSlotLocked, setIsSlotLocked] = useState(initialIsLocked);

  const [selectedType, setSelectedType] = useState<AdType | null>(initialSelectedType);
  const [selectedSlot, setSelectedSlot] = useState<PlacementSlot | null>(initialSelectedSlot);
  const [adModel, setAdModel] = useState<AdModel>('free');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [hoveredSlot, setHoveredSlot] = useState<PlacementSlot | null>(null);

  const [dailyRates, setDailyRates] = useState<Record<AdType, number>>(defaultDailyRates);
  const [adminUsdtAddress, setAdminUsdtAddress] = useState('');

  const [form, setForm] = useState({
    projectName: '',
    projectUrl: '',
    projectCategory: 'Crypto',
    headline: '',
    description: '',
    ctaText: 'Learn More',
    durationDays: 30,
    advertiserWebsiteUrl: '',
    freeAdNotes: '',
  });

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      setUser(data?.user ?? null);
      setCheckingAuth(false);
    });
    supabase
      .from('app_settings')
      .select('key, value')
      .in('key', [
        'daily_rate_banner',
        'daily_rate_featured_listing',
        'daily_rate_sponsored_post',
        'daily_rate_mini_card',
        'daily_rate_side_strip',
        'daily_rate_flash_banner',
        'admin_usdt_address',
      ])
      .then(({ data }) => {
        if (data) {
          const rates = { ...defaultDailyRates };
          data.forEach(row => {
            if (row.key === 'daily_rate_banner') rates.banner = parseFloat(row.value) || 10;
            if (row.key === 'daily_rate_featured_listing') rates.featured_listing = parseFloat(row.value) || 7;
            if (row.key === 'daily_rate_sponsored_post') rates.sponsored_post = parseFloat(row.value) || 4;
            if (row.key === 'daily_rate_mini_card') rates.mini_card = parseFloat(row.value) || 3;
            if (row.key === 'daily_rate_side_strip') rates.side_strip = parseFloat(row.value) || 5;
            if (row.key === 'daily_rate_flash_banner') rates.flash_banner = parseFloat(row.value) || 8;
            if (row.key === 'admin_usdt_address') setAdminUsdtAddress(row.value);
          });
          setDailyRates(rates);
        }
      });
  }, []);

  const handleContinueToPreview = () => {
    if (!selectedType || !selectedSlot) {
      toast.error('Please select an ad type and placement slot');
      return;
    }
    if (!user) {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('pending_ad_type', selectedType);
        sessionStorage.setItem('pending_ad_slot', selectedSlot);
      }
      router.push('/login?redirect=/place-ad&reason=ad');
      return;
    }
    setStep(3);
  };

  const handleContinueToForm = () => {
    setStep(4);
  };

  useEffect(() => {
    if (user && typeof window !== 'undefined') {
      const pendingType = sessionStorage.getItem('pending_ad_type') as AdType | null;
      const pendingSlot = sessionStorage.getItem('pending_ad_slot') as PlacementSlot | null;
      if (pendingType && pendingSlot) {
        setSelectedType(pendingType);
        setSelectedSlot(pendingSlot);
        sessionStorage.removeItem('pending_ad_type');
        sessionStorage.removeItem('pending_ad_slot');
        // If arriving from a locked slot, go to form (step 4); otherwise preview (step 3)
        const slotData = allPlacementSlots.find(s => s.id === pendingSlot);
        if (slotData) {
          setIsSlotLocked(true);
          setStep(4);
        } else {
          setStep(3);
        }
      }
    }
  }, [user]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Screenshot must be under 5MB'); return; }
    setScreenshotFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setScreenshotPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => { setImageFile(null); setImagePreview(null); };
  const removeScreenshot = () => { setScreenshotFile(null); setScreenshotPreview(null); };

  const getDailyRate = () => selectedType ? (dailyRates[selectedType] ?? defaultDailyRates[selectedType] ?? 4) : 0;
  const getTotalUsdt = () => getDailyRate() * form.durationDays;

  const selectedTypeData = allAdTypes.find(t => t.id === selectedType);
  const selectedSlotData = allPlacementSlots.find(s => s.id === selectedSlot);
  const isSocialImageAd = selectedSlot === 'community_feed' && adModel === 'free';

  // When ad type changes, clear slot if incompatible (only in non-locked mode)
  const handleSelectType = (typeId: AdType) => {
    setSelectedType(typeId);
    if (!isSlotLocked && selectedSlot) {
      const slotData = allPlacementSlots.find(s => s.id === selectedSlot);
      if (slotData && !slotData.compatibleTypes.includes(typeId)) {
        setSelectedSlot(null);
      }
    }
  };

  // Determine which page to show in the visualizer
  const activeSlotForViz = hoveredSlot ?? selectedSlot;
  const vizPage = activeSlotForViz
    ? (slotPositions[activeSlotForViz]?.page ?? 'Home')
    : selectedSlot
    ? (slotPositions[selectedSlot]?.page ?? 'Home')
    : 'Home';

  const handleSubmit = async () => {
    if (!user) {
      const destination = selectedSlot ? `/place-ad?slot=${selectedSlot}` : '/place-ad';
      router.push(`/login?redirect=${encodeURIComponent(destination)}&reason=ad`);
      return;
    }
    let websiteUrl = form.projectUrl.trim();
    if (isSocialImageAd) {
      if (!imageFile) { toast.error('Please upload your ad image'); return; }
      try {
        const website = new URL(websiteUrl);
        if (!['https:', 'http:'].includes(website.protocol)) throw new Error('Invalid website');
        websiteUrl = website.href;
      } catch { toast.error('Enter a valid website URL starting with https:// or http://'); return; }
    } else if (!form.projectName || !form.headline) { toast.error('Please fill in all required fields'); return; }
    if (adModel === 'free_exchange' && !screenshotFile) {
      toast.error('Please upload a screenshot of BizzBox ad on your website');
      return;
    }
    setSubmitting(true);
    try {
      const supabase = createClient();
      let imageUrl: string | null = null;
      let screenshotUrl: string | null = null;

      if (imageFile) {
        setUploadingImage(true);
        const ext = imageFile.name.split('.').pop();
        const fileName = `${user.id}_${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('ad-images')
          .upload(fileName, imageFile, { upsert: true });
        setUploadingImage(false);
        if (uploadError || !uploadData) throw new Error('Image upload failed. Please try again.');
        if (!uploadError && uploadData) {
          const { data: urlData } = supabase.storage.from('ad-images').getPublicUrl(uploadData.path);
          imageUrl = urlData?.publicUrl ?? null;
        }
      }

      if (screenshotFile) {
        const ext = screenshotFile.name.split('.').pop();
        const fileName = `screenshots/${user.id}_${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('ad-images')
          .upload(fileName, screenshotFile, { upsert: true });
        if (!uploadError && uploadData) {
          const { data: urlData } = supabase.storage.from('ad-images').getPublicUrl(uploadData.path);
          screenshotUrl = urlData?.publicUrl ?? null;
        }
      }

      const dailyRate = getDailyRate();
      const totalUsdt = getTotalUsdt();
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + form.durationDays);

      const { error } = await supabase.from('ad_placements').insert({
        owner_id: user.id,
        advertiser_id: user.id,
        project_name: isSocialImageAd ? new URL(websiteUrl).hostname : form.projectName,
        project_url: websiteUrl || null,
        project_category: form.projectCategory,
        ad_type: selectedType,
        placement_slot: selectedSlot,
        ad_status: 'pending_review',
        headline: isSocialImageAd ? 'Sponsored image' : form.headline,
        image_only: isSocialImageAd,
        description: form.description || null,
        cta_text: form.ctaText || 'Learn More',
        image_url: imageUrl,
        budget: adModel === 'paid' ? totalUsdt : 0,
        duration_days: adModel === 'free' ? 1 : form.durationDays,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        payment_model: adModel,
        daily_rate: adModel === 'paid' ? dailyRate : 0,
        total_usdt: adModel === 'paid' ? totalUsdt : 0,
        payment_confirmed: false,
        advertiser_website_url: adModel === 'free_exchange' ? form.advertiserWebsiteUrl || null : null,
        screenshot_url: screenshotUrl,
        free_ad_notes: adModel === 'free_exchange' ? form.freeAdNotes || null : null,
      });

      if (error) {
        console.error('Ad submission error:', error);
        toast.error(`Failed to submit ad: ${error.message}`);
      } else {
        setSubmitted(true);
        setStep(5);
      }
    } catch (err) {
      console.error('Ad submission unexpected error:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Step 5: Success
  if (step === 5 && submitted) {
    return (
      <div className="max-w-lg mx-auto py-8 px-4 text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto">
          <CheckCircleIcon style={{ width: 32, height: 32 }} className="text-green-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground mb-2">Ad Submitted for Review</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {adModel === 'paid' ?'Please send the USDT payment to the address below. Once admin confirms your payment, your ad will be activated.' : adModel === 'free' ? 'Your free ad is awaiting admin approval. It will run for 24 hours after approval.' :'Your free exchange ad request has been submitted. Admin will review your screenshot and assign a placement slot.'}
          </p>
        </div>

        {/* Placement confirmation */}
        {selectedSlotData && selectedTypeData && (
          <div
            className="w-full rounded-xl border p-4 text-left space-y-3"
            style={{ borderColor: `${pageColors[selectedSlotData.page] ?? '#6366f1'}40`, backgroundColor: `${pageColors[selectedSlotData.page] ?? '#6366f1'}08` }}
          >
            <div className="flex items-center gap-2">
              <MapPinIcon style={{ width: 14, height: 14, color: pageColors[selectedSlotData.page] ?? '#6366f1' }} />
              <span className="text-xs font-bold text-foreground uppercase tracking-wider">Your Ad Placement</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-left">
              <div className="p-2.5 bg-background/60 rounded-lg border border-border">
                <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Page</p>
                <p className="text-xs font-bold text-foreground">{selectedSlotData.page} Page</p>
                <p className="text-[10px] font-mono text-muted-foreground">bizzbox.com{pageRoutes[selectedSlotData.page] ?? '/'}</p>
              </div>
              <div className="p-2.5 bg-background/60 rounded-lg border border-border">
                <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Section</p>
                <p className="text-xs font-bold text-foreground">{slotPositionLabels[selectedSlotData.id]?.section}</p>
                <p className="text-[10px] text-muted-foreground">{selectedSlotData.label}</p>
              </div>
              <div className="col-span-2 p-2.5 bg-background/60 rounded-lg border border-border">
                <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Exact Position</p>
                <p className="text-xs font-semibold text-foreground">{slotPositionLabels[selectedSlotData.id]?.position}</p>
              </div>
            </div>
          </div>
        )}

        {adModel === 'paid' && adminUsdtAddress && (
          <div className="bg-green-500/8 border border-green-500/20 rounded-xl p-4 text-left space-y-3">
            <div className="flex items-center gap-2">
              <WalletIcon style={{ width: 16, height: 16 }} className="text-green-400" />
              <span className="text-sm font-semibold text-foreground">Send Payment to Admin USDT Wallet</span>
            </div>
            <div className="bg-background rounded-lg p-3 border border-border">
              <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider font-semibold">USDT Address</p>
              <p className="text-sm font-mono text-foreground break-all">{adminUsdtAddress}</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Amount to send:</span>
              <span className="text-lg font-bold text-green-400">{getTotalUsdt()} USDT</span>
            </div>
            <p className="text-[10px] text-muted-foreground">After sending, notify the admin. Once payment is confirmed, your ad will be activated.</p>
          </div>
        )}

        <div className="bg-card border border-border rounded-xl p-4 text-left space-y-3">
          <div className="flex items-center gap-2">
            <ClockIcon style={{ width: 16, height: 16 }} className="text-amber-400" />
            <span className="text-sm font-semibold text-foreground">What happens next?</span>
          </div>
          <div className="space-y-2 text-xs text-muted-foreground">
            {adModel === 'paid' ? (
              <>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <span>Send {getTotalUsdt()} USDT to the wallet address above</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                  <span>Admin confirms your payment and activates the ad</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <span>Your ad goes live for {form.durationDays} days on the selected placement</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <span>{adModel === 'free' ? 'Admin reviews your ad' : 'Admin reviews your screenshot proof'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                  <span>Admin assigns a placement slot and approves your listing</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <span>{adModel === 'free' ? 'Your free ad goes live for 24 hours' : 'Your free ad goes live for 30 days'}</span>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <Link href="/" className="flex-1 py-2.5 rounded-xl text-center text-sm font-semibold text-muted-foreground border border-border hover:bg-muted transition-colors">
            Back to Home
          </Link>
          <Link href="/advertisement-manager" className="flex-1 py-2.5 rounded-xl text-center text-sm font-semibold text-white bg-primary hover:opacity-90 transition-opacity">
            View My Ads
          </Link>
        </div>
      </div>
    );
  }

  // Step labels — when slot is locked, show a focused 3-step flow
  if (isSocialImageAd) {
    return (
      <div className="mx-auto max-w-lg space-y-4">
        <h1 className="text-xl font-bold text-foreground">Place an ad in Activity</h1>
        <p className="text-sm text-muted-foreground">Upload your complete ad image and add your website. After admin approval, your free ad will run for 24 hours.</p>
        <form onSubmit={(event) => { event.preventDefault(); void handleSubmit(); }} className="space-y-4 rounded-xl border border-border bg-card p-4">
          <label className="block text-sm font-semibold text-foreground">
            Ad image
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageChange} disabled={submitting} className="mt-2 block w-full min-w-0 text-xs" />
          </label>
          <p className="text-xs text-muted-foreground">JPG, PNG, WebP or GIF, up to 2 MB. Your full image will be displayed without cropping.</p>
          {imagePreview && <img src={imagePreview} alt="Ad preview" className="h-auto w-full rounded-lg" />}
          <label className="block text-sm font-semibold text-foreground">
            Website URL
            <input type="url" required placeholder="https://yourwebsite.com" value={form.projectUrl} onChange={(event) => setForm((previous) => ({ ...previous, projectUrl: event.target.value }))} disabled={submitting} className="mt-2 w-full rounded-lg border border-border bg-input px-3 py-2 text-sm" />
          </label>
          <button type="submit" disabled={checkingAuth || submitting || !imageFile || !form.projectUrl.trim()} className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
            {submitting ? 'Submitting...' : user ? 'Submit for admin review' : 'Sign in to submit'}
          </button>
        </form>
        <button onClick={() => setAdModel('free_exchange')} className="text-xs text-primary hover:underline">Choose Free Exchange instead</button>
      </div>
    );
  }

  const stepLabels = isSlotLocked
    ? ['Ad Model', 'Ad Details', 'Preview']
    : ['Ad Type', 'Placement', 'Preview', 'Ad Details'];

  // Map actual step number to display step index for the indicator
  const getDisplayStep = () => {
    if (isSlotLocked) {
      // locked: step 1 = Ad Model (1), step 4 = Ad Details (2), step 3 = Preview (3)
      if (step === 1) return 1;
      if (step === 4) return 2;
      if (step === 3) return 3;
      return 1;
    }
    return step;
  };

  const displayStep = getDisplayStep();

  return (
    <div className="max-w-2xl mx-auto pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 pt-1 mb-6">
        <Link href="/" className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeftIcon style={{ width: 18, height: 18 }} />
        </Link>
        <div>
          <h1 className="text-lg font-bold text-foreground">Place an Advertisement</h1>
          <p className="text-xs text-muted-foreground">Reach thousands of investors and traders</p>
        </div>
      </div>

      {/* Context banner when arriving from a specific page — hide on locked step 1 since slot summary is shown there */}
      {preselectedFromPage && step !== 5 && !(isSlotLocked && step === 1) && (
        <div className="flex items-start gap-3 p-3.5 bg-primary/5 border border-primary/20 rounded-xl mb-5">
          <MegaphoneIcon style={{ width: 16, height: 16 }} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground">
              Advertising on the <span className="text-primary">{preselectedFromPage} page</span>
              {selectedSlotData && <span className="text-muted-foreground font-normal"> — {selectedSlotData.label}</span>}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Showing only the rate plan and placement relevant to this slot.
            </p>
          </div>
          <button
            onClick={() => { setPreselectedFromPage(null); setSelectedSlot(null); setSelectedType(null); setIsSlotLocked(false); setStep(1); }}
            className="text-[10px] text-primary hover:underline flex-shrink-0 font-medium"
          >
            See all options
          </button>
        </div>
      )}

      {/* Step Indicator */}
      <div className="flex items-center gap-0 mb-6">
        {stepLabels.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                displayStep > i + 1 ? 'bg-green-500 text-white' : displayStep === i + 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {displayStep > i + 1 ? '✓' : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${displayStep === i + 1 ? 'text-foreground' : 'text-muted-foreground'}`}>{s}</span>
            </div>
            {i < stepLabels.length - 1 && <div className="flex-1 h-px bg-border mx-2" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 1: Choose Ad Type (only shown when NOT slot-locked) ── */}
      {step === 1 && !isSlotLocked && (
        <div className="space-y-5">
          {/* Ad Model Toggle */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ad Model</p>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <button onClick={() => setAdModel('free')} className={`rounded-xl border p-3.5 text-left ${adModel === 'free' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <span className="text-sm font-semibold text-foreground">Free Ad - 24 hours</span>
                <p className="mt-1 text-xs text-muted-foreground">No payment or exchange proof required. Runs for 24 hours after approval.</p>
              </button>
              {/* Paid Ad temporarily hidden.
              <button
                onClick={() => setAdModel('paid')}
                className={`flex flex-col items-start gap-1.5 p-3.5 rounded-xl border text-left transition-all ${
                  adModel === 'paid' ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/30'
                }`}
              >
                <div className="flex items-center gap-2 w-full">
                  <CurrencyDollarIcon style={{ width: 16, height: 16 }} className={adModel === 'paid' ? 'text-primary' : 'text-muted-foreground'} />
                  <span className="text-sm font-semibold text-foreground">Paid Ad</span>
                  {adModel === 'paid' && <CheckCircleIcon style={{ width: 14, height: 14 }} className="text-primary ml-auto" />}
                </div>
                <p className="text-[10px] text-muted-foreground">Pay in USDT based on days × daily rate. Admin activates after payment confirmation.</p>
              </button>
              */}
              <button
                onClick={() => setAdModel('free_exchange')}
                className={`flex flex-col items-start gap-1.5 p-3.5 rounded-xl border text-left transition-all ${
                  adModel === 'free_exchange' ? 'border-purple-500/50 bg-purple-500/5' : 'border-border bg-background hover:border-purple-500/30'
                }`}
              >
                <div className="flex items-center gap-2 w-full">
                  <CameraIcon style={{ width: 16, height: 16 }} className={adModel === 'free_exchange' ? 'text-purple-400' : 'text-muted-foreground'} />
                  <span className="text-sm font-semibold text-foreground">Free Exchange</span>
                  {adModel === 'free_exchange' && <CheckCircleIcon style={{ width: 14, height: 14 }} className="text-purple-400 ml-auto" />}
                </div>
                <p className="text-[10px] text-muted-foreground">Place BizzBox's ad on your website and submit a screenshot as proof. Free for 30 days.</p>
              </button>
            </div>
          </div>

          {/* Ad Type Categories */}
          <div className="space-y-5">
            {adTypeCategories.map((cat) => {
              const CatIcon = cat.icon;
              const typesInCat = allAdTypes.filter(t => cat.types.includes(t.id));
              return (
                <div key={cat.label} className="space-y-2">
                  {/* Category Header */}
                  <div className="flex items-center gap-2 px-1">
                    <div className="p-1.5 rounded-lg bg-muted">
                      <CatIcon style={{ width: 14, height: 14 }} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground">{cat.label}</p>
                      <p className="text-[10px] text-muted-foreground">{cat.description}</p>
                    </div>
                  </div>

                  {/* Types in this category */}
                  <div className="space-y-2">
                    {typesInCat.map((type) => {
                      const TypeIcon = type.icon;
                      const isSelected = selectedType === type.id;
                      return (
                        <button
                          key={type.id}
                          onClick={() => handleSelectType(type.id)}
                          className={`w-full flex items-start gap-4 p-4 rounded-xl border text-left transition-all ${
                            isSelected ? `border-primary bg-primary/5` : 'border-border bg-card hover:border-primary/30'
                          }`}
                        >
                          <div className={`p-2.5 rounded-lg ${type.bgColor} flex-shrink-0`}>
                            <TypeIcon style={{ width: 18, height: 18 }} className={type.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-foreground text-sm">{type.name}</span>
                              {adModel === 'paid' ? (
                                <span className="text-sm font-bold text-primary">
                                  ${dailyRates[type.id] ?? type.dailyRate}<span className="text-xs text-muted-foreground font-normal ml-1">/day</span>
                                </span>
                              ) : (
                                <span className="text-sm font-bold text-purple-400">Free</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-[10px] text-muted-foreground">Est. {type.impressions} impressions</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${type.bgColor} ${type.color}`}>{type.size}</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {type.features.slice(0, 3).map((f) => (
                                <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{f}</span>
                              ))}
                            </div>
                          </div>
                          {isSelected && <CheckCircleIcon className="text-primary flex-shrink-0 mt-0.5" style={{ width: 18, height: 18 }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={() => selectedType && setStep(2)}
            disabled={!selectedType}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continue to Placement Selection
          </button>
        </div>
      )}

      {/* ── Step 2: Choose Placement Slot (or locked single-slot view) ── */}
      {step === 2 && (
        <div className="space-y-4">
          {/* ── LOCKED MODE: show only the single relevant rate plan and the one slot ── */}
          {isSlotLocked ? (
            <div className="space-y-4">
              {/* Ad Model Toggle */}
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ad Model</p>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <button onClick={() => setAdModel('free')} className={`rounded-xl border p-3.5 text-left ${adModel === 'free' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <span className="text-sm font-semibold text-foreground">Free Ad - 24 hours</span>
                <p className="mt-1 text-xs text-muted-foreground">No payment or exchange proof required. Runs for 24 hours after approval.</p>
              </button>
              {/* Paid Ad temporarily hidden.
                  <button
                    onClick={() => setAdModel('paid')}
                    className={`flex flex-col items-start gap-1.5 p-3.5 rounded-xl border text-left transition-all ${
                      adModel === 'paid' ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <CurrencyDollarIcon style={{ width: 16, height: 16 }} className={adModel === 'paid' ? 'text-primary' : 'text-muted-foreground'} />
                      <span className="text-sm font-semibold text-foreground">Paid Ad</span>
                      {adModel === 'paid' && <CheckCircleIcon style={{ width: 14, height: 14 }} className="text-primary ml-auto" />}
                    </div>
                    <p className="text-[10px] text-muted-foreground">Pay in USDT based on days × daily rate.</p>
                  </button>
                  */}
                  <button
                    onClick={() => setAdModel('free_exchange')}
                    className={`flex flex-col items-start gap-1.5 p-3.5 rounded-xl border text-left transition-all ${
                      adModel === 'free_exchange' ? 'border-purple-500/50 bg-purple-500/5' : 'border-border bg-background hover:border-purple-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <CameraIcon style={{ width: 16, height: 16 }} className={adModel === 'free_exchange' ? 'text-purple-400' : 'text-muted-foreground'} />
                      <span className="text-sm font-semibold text-foreground">Free Exchange</span>
                      {adModel === 'free_exchange' && <CheckCircleIcon style={{ width: 14, height: 14 }} className="text-purple-400 ml-auto" />}
                    </div>
                    <p className="text-[10px] text-muted-foreground">Place BizzBox's ad on your site as proof. Free for 30 days.</p>
                  </button>
                </div>
              </div>

              {selectedTypeData && (
                <>
                  {/* Single Rate Plan Card */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">Rate Plan for This Slot</p>
                    <div className={`flex items-start gap-4 p-4 rounded-xl border border-primary bg-primary/5`}>
                      <div className={`p-2.5 rounded-lg ${selectedTypeData.bgColor} flex-shrink-0`}>
                        <selectedTypeData.icon style={{ width: 18, height: 18 }} className={selectedTypeData.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-foreground text-sm">{selectedTypeData.name}</span>
                          {adModel === 'paid' ? (
                            <span className="text-sm font-bold text-primary">
                              ${dailyRates[selectedTypeData.id] ?? selectedTypeData.dailyRate}<span className="text-xs text-muted-foreground font-normal ml-1">/day</span>
                            </span>
                          ) : (
                            <span className="text-sm font-bold text-purple-400">Free</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[10px] text-muted-foreground">Est. {selectedTypeData.impressions} impressions</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${selectedTypeData.bgColor} ${selectedTypeData.color}`}>{selectedTypeData.size}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedTypeData.features.slice(0, 3).map((f) => (
                            <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{f}</span>
                          ))}
                        </div>
                      </div>
                      <CheckCircleIcon className="text-primary flex-shrink-0 mt-0.5" style={{ width: 18, height: 18 }} />
                    </div>
                  </div>
                </>
              )}

              {selectedSlotData && (
                <>
                  {/* Single Placement Slot (locked) */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">Placement — {selectedSlotData.page} Page</p>
                    <div className="rounded-xl border border-primary bg-primary/5 overflow-hidden">
                      <div className="flex items-start gap-3 p-3">
                        <div
                          className="w-1 self-stretch rounded-full flex-shrink-0"
                          style={{ backgroundColor: pageColors[selectedSlotData.page] ?? '#6366f1', minHeight: 36 }}
                        />
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${pageColors[selectedSlotData.page] ?? '#6366f1'}18` }}
                        >
                          <selectedSlotData.pageIcon
                            style={{ width: 15, height: 15, color: pageColors[selectedSlotData.page] ?? '#6366f1' }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            <p className="text-sm font-semibold text-foreground leading-tight">{selectedSlotData.label}</p>
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-400 flex-shrink-0">✓ Selected</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground line-clamp-1 mb-1.5">{selectedSlotData.description}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[9px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{selectedSlotData.size}</span>
                            <span className={`text-[9px] font-semibold ${selectedSlotData.visibilityColor}`}>● {selectedSlotData.visibility} Visibility</span>
                            <span
                              className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                              style={{ backgroundColor: `${pageColors[selectedSlotData.page] ?? '#6366f1'}15`, color: pageColors[selectedSlotData.page] ?? '#6366f1' }}
                            >
                              {selectedSlotData.page}
                            </span>
                          </div>
                        </div>
                        <CheckCircleIcon className="text-primary flex-shrink-0 mt-0.5" style={{ width: 16, height: 16 }} />
                      </div>
                      <div className="px-3 pb-2.5 flex items-center gap-1.5">
                        <MapPinIcon style={{ width: 10, height: 10 }} className="text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              backgroundColor: pageColors[selectedSlotData.page] ?? '#6366f1',
                              width: selectedSlotData.visibility === 'High' ? '85%' : selectedSlotData.visibility === 'Medium' ? '55%' : '30%',
                            }}
                          />
                        </div>
                        <span className="text-[9px] text-muted-foreground flex-shrink-0">
                          {selectedSlotData.visibility === 'High' ? 'Prime spot' : selectedSlotData.visibility === 'Medium' ? 'Good reach' : 'Niche audience'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Page Layout Visualizer for the locked page */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <EyeIcon style={{ width: 13, height: 13 }} className="text-primary" />
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ad Location on {selectedSlotData.page} Page</p>
                    </div>
                    <PageLayoutVisualizer
                      page={selectedSlotData.page}
                      highlightSlot={selectedSlot}
                      hoveredSlot={null}
                    />
                  </div>

                  {/* Placement Info Panel */}
                  {selectedTypeData && (
                    <AdPlacementInfoPanel slotData={selectedSlotData} adTypeData={selectedTypeData} />
                  )}
                </>
              )}

              {!user && (
                <div className="flex items-start gap-3 p-3.5 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                  <LockClosedIcon style={{ width: 16, height: 16 }} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">Login required to continue</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">You'll be asked to sign in before filling in your ad details.</p>
                  </div>
                </div>
              )}

              <button
                onClick={handleContinueToPreview}
                className="w-full py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                {!user ? (
                  <><LockClosedIcon style={{ width: 14, height: 14 }} />Sign In to Continue</>
                ) : (
                  <><EyeIcon style={{ width: 14, height: 14 }} />Preview Ad</>
                )}
              </button>
            </div>
          ) : (
            /* ── NORMAL MODE: full slot selection ── */
            <div className="space-y-4">
              {/* Selected type summary */}
              {selectedTypeData && (
                <div className={`flex items-center gap-3 p-3 rounded-xl border ${selectedTypeData.borderColor} bg-card`}>
                  <div className={`p-2 rounded-lg ${selectedTypeData.bgColor}`}>
                    <selectedTypeData.icon style={{ width: 16, height: 16 }} className={selectedTypeData.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Selected Ad Type</p>
                    <p className="text-sm font-semibold text-foreground">{selectedTypeData.name}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-lg font-mono ${selectedTypeData.bgColor} ${selectedTypeData.color}`}>{selectedTypeData.size}</span>
                  <button onClick={() => setStep(1)} className="text-xs text-primary hover:underline flex-shrink-0">Change</button>
                </div>
              )}

              {/* Visual layout + slot list */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Left: Slot list */}
                <div className="lg:col-span-3 space-y-4">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Choose Placement</p>
                    <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Hover to preview location</span>
                  </div>

                  {placementCategories.map((cat) => {
                    const CatIcon = cat.icon;
                    const slotsInCat = allPlacementSlots.filter(s => cat.slots.includes(s.id));
                    const hasCompatible = slotsInCat.some(s => !selectedType || s.compatibleTypes.includes(selectedType));

                    return (
                      <div key={cat.label} className="space-y-2">
                        <div className="flex items-center gap-2 px-1">
                          <div className="p-1.5 rounded-lg bg-muted">
                            <CatIcon style={{ width: 13, height: 13 }} className={cat.color} />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-bold text-foreground">{cat.label}</p>
                          </div>
                          {!hasCompatible && (
                            <span className="text-[9px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">No compatible slots</span>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          {slotsInCat.map((slot) => {
                            const isSelected = selectedSlot === slot.id;
                            const isCompatible = !selectedType || slot.compatibleTypes.includes(selectedType);
                            const isHovered = hoveredSlot === slot.id;

                            return (
                              <SlotCard
                                key={slot.id}
                                slot={slot}
                                isSelected={isSelected}
                                isCompatible={isCompatible}
                                isHovered={isHovered}
                                onSelect={() => setSelectedSlot(slot.id)}
                                onHover={setHoveredSlot}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right: Visual page layout */}
                <div className="lg:col-span-2 space-y-3">
                  <div className="sticky top-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <EyeIcon style={{ width: 13, height: 13 }} className="text-primary" />
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ad Location Preview</p>
                    </div>

                    <PageLayoutVisualizer
                      page={vizPage}
                      highlightSlot={selectedSlot}
                      hoveredSlot={hoveredSlot}
                    />

                    {/* Legend */}
                    <div className="bg-card border border-border rounded-xl p-3 space-y-2">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Page Colors</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {Object.entries(pageColors).map(([pg, color]) => (
                          <div key={pg} className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                            <span className="text-[10px] text-muted-foreground">{pg}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Selected slot detail */}
                    {(hoveredSlot ?? selectedSlot) && (() => {
                      const activeId = hoveredSlot ?? selectedSlot;
                      const slotData = allPlacementSlots.find(s => s.id === activeId);
                      if (!slotData) return null;
                      const color = pageColors[slotData.page] ?? '#6366f1';
                      return (
                        <div
                          className="rounded-xl p-3 border space-y-1.5"
                          style={{ borderColor: `${color}40`, backgroundColor: `${color}08` }}
                        >
                          <div className="flex items-center gap-2">
                            <MapPinIcon style={{ width: 13, height: 13, color }} />
                            <p className="text-xs font-bold text-foreground">{slotData.label}</p>
                          </div>
                          <p className="text-[11px] text-muted-foreground">{slotData.description}</p>
                          <div className="flex items-center gap-2 pt-1">
                            <span className="text-[9px] font-mono bg-background/60 px-1.5 py-0.5 rounded text-muted-foreground">{slotData.size}</span>
                            <span className={`text-[9px] font-semibold ${slotData.visibilityColor}`}>● {slotData.visibility} Visibility</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {!user && (
                <div className="flex items-start gap-3 p-3.5 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                  <LockClosedIcon style={{ width: 16, height: 16 }} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">Login required to continue</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">You'll be asked to sign in or create an account before filling in your ad details.</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="px-4 py-2.5 rounded-xl bg-muted text-muted-foreground hover:text-foreground text-sm font-semibold transition-colors">
                  Back
                </button>
                <button
                  onClick={handleContinueToPreview}
                  disabled={!selectedSlot}
                  className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {!user ? (
                    <><LockClosedIcon style={{ width: 14, height: 14 }} />Sign In to Continue</>
                  ) : (
                    <><EyeIcon style={{ width: 14, height: 14 }} />Preview Ad</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 3: Ad Preview ── */}
      {step === 3 && selectedTypeData && selectedSlotData && (
        <div className="space-y-5">
          {/* Summary bar */}
          <div className="flex items-center gap-2 p-3 bg-card border border-border rounded-xl flex-wrap">
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${selectedTypeData.bgColor}`}>
              <selectedTypeData.icon style={{ width: 13, height: 13 }} className={selectedTypeData.color} />
              <span className={`text-[11px] font-semibold ${selectedTypeData.color}`}>{selectedTypeData.name}</span>
            </div>
            <span className="text-muted-foreground text-xs">→</span>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted">
              <selectedSlotData.pageIcon style={{ width: 13, height: 13 }} className="text-muted-foreground" />
              <span className="text-[11px] font-semibold text-foreground">{selectedSlotData.label}</span>
            </div>
            <span className="text-muted-foreground text-xs">→</span>
            <div className={`px-2.5 py-1.5 rounded-lg ${adModel === 'free_exchange' ? 'bg-purple-500/10' : 'bg-primary/10'}`}>
              <span className={`text-[11px] font-semibold ${adModel === 'free_exchange' ? 'text-purple-400' : 'text-primary'}`}>
                {adModel === 'free_exchange' ? 'Free Exchange' : adModel === 'free' ? 'Free Ad - 24 hours' : 'Paid (USDT)'}
              </span>
            </div>
          </div>

          {/* Placement Info Panel */}
          <AdPlacementInfoPanel slotData={selectedSlotData} adTypeData={selectedTypeData} />

          {/* Ad Preview */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <EyeIcon style={{ width: 14, height: 14 }} className="text-primary" />
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Ad Preview</span>
              <span className="text-[10px] text-muted-foreground ml-auto">How your ad will look</span>
            </div>
            <AdPreview
              adType={selectedType}
              headline={isSlotLocked ? form.headline : ''}
              description={isSlotLocked ? form.description : ''}
              ctaText={isSlotLocked ? form.ctaText : 'Learn More'}
              imagePreview={isSlotLocked ? imagePreview : null}
              projectName={isSlotLocked ? form.projectName : ''}
              placementLabel={selectedSlotData.label}
            />
            {!isSlotLocked && (
              <p className="text-[10px] text-muted-foreground text-center">
                This is a sample preview. You'll add your headline, description, and image in the next step.
              </p>
            )}
          </div>

          {/* Pricing summary */}
          {adModel === 'paid' && (
            <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <CurrencyDollarIcon style={{ width: 15, height: 15 }} className="text-green-400" />
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Pricing</span>
              </div>
              {isSlotLocked ? (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{form.durationDays} days × ${getDailyRate()} USDT/day</span>
                  <span className="text-lg font-bold text-green-400">{getTotalUsdt()} <span className="text-xs text-muted-foreground font-normal">USDT total</span></span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Daily rate for {selectedTypeData.name}</span>
                    <span className="text-lg font-bold text-green-400">${getDailyRate()} <span className="text-xs text-muted-foreground font-normal">USDT/day</span></span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">You'll set the number of days in the next step.</p>
                </>
              )}
            </div>
          )}

          {adModel === 'free_exchange' && (
            <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <CameraIcon style={{ width: 14, height: 14 }} className="text-purple-400" />
                <span className="text-xs font-semibold text-purple-400">Free Exchange — 30 days free</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Place BizzBox's ad on your website and upload a screenshot as proof in the next step.</p>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => setStep(isSlotLocked ? 4 : 2)} className="px-4 py-2.5 rounded-xl bg-muted text-muted-foreground hover:text-foreground text-sm font-semibold transition-colors">
              Back
            </button>
            {isSlotLocked ? (
              <button
                onClick={handleSubmit}
                disabled={submitting || !form.projectName || !form.headline || (adModel === 'free_exchange' && !screenshotFile)}
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting && <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />}
                {adModel === 'paid' ? `Submit for Review — ${getTotalUsdt()} USDT` : 'Submit for Review'}
              </button>
            ) : (
              <button
                onClick={handleContinueToForm}
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                Continue to Ad Details
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Step 1 (Locked Mode): Choose Ad Model only ── */}
      {step === 1 && isSlotLocked && (
        <div className="space-y-5">
          {/* Slot summary */}
          {selectedSlotData && selectedTypeData && (
            <div className="flex items-center gap-3 p-3.5 bg-primary/5 border border-primary/20 rounded-xl">
              <MegaphoneIcon style={{ width: 16, height: 16 }} className="text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground">
                  Booking: <span className="text-primary">{selectedSlotData.label}</span>
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {selectedSlotData.page} Page · {selectedSlotData.size} · {selectedTypeData.name}
                </p>
              </div>
              <button
                onClick={() => { setPreselectedFromPage(null); setSelectedSlot(null); setSelectedType(null); setIsSlotLocked(false); setStep(1); }}
                className="text-[10px] text-primary hover:underline flex-shrink-0 font-medium"
              >
                See all options
              </button>
            </div>
          )}

          {/* Ad Model Selection */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <p className="text-sm font-bold text-foreground">AD MODEL</p>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <button onClick={() => setAdModel('free')} className={`rounded-xl border p-3.5 text-left ${adModel === 'free' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <span className="text-sm font-semibold text-foreground">Free Ad - 24 hours</span>
                <p className="mt-1 text-xs text-muted-foreground">No payment or exchange proof required. Runs for 24 hours after approval.</p>
              </button>
              {/* Paid Ad temporarily hidden.
              <button
                onClick={() => setAdModel('paid')}
                className={`flex flex-col items-start gap-1.5 p-3.5 rounded-xl border text-left transition-all ${
                  adModel === 'paid' ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/30'
                }`}
              >
                <div className="flex items-center gap-2 w-full">
                  <CurrencyDollarIcon style={{ width: 16, height: 16 }} className={adModel === 'paid' ? 'text-primary' : 'text-muted-foreground'} />
                  <span className="text-sm font-semibold text-foreground">Paid Ad</span>
                  {adModel === 'paid' && <CheckCircleIcon style={{ width: 14, height: 14 }} className="text-primary ml-auto" />}
                </div>
                <p className="text-[10px] text-muted-foreground">Pay in USDT based on days × daily rate. Admin activates after payment confirmation.</p>
              </button>
              */}
              <button
                onClick={() => setAdModel('free_exchange')}
                className={`flex flex-col items-start gap-1.5 p-3.5 rounded-xl border text-left transition-all ${
                  adModel === 'free_exchange' ? 'border-purple-500/50 bg-purple-500/5' : 'border-border bg-background hover:border-purple-500/30'
                }`}
              >
                <div className="flex items-center gap-2 w-full">
                  <CameraIcon style={{ width: 16, height: 16 }} className={adModel === 'free_exchange' ? 'text-purple-400' : 'text-muted-foreground'} />
                  <span className="text-sm font-semibold text-foreground">Free Exchange</span>
                  {adModel === 'free_exchange' && <CheckCircleIcon style={{ width: 14, height: 14 }} className="text-purple-400 ml-auto" />}
                </div>
                <p className="text-[10px] text-muted-foreground">Place BizzBox's ad on your website and submit a screenshot as proof. Free for 30 days.</p>
              </button>
            </div>
          </div>

          {!user && (
            <div className="flex items-start gap-3 p-3.5 bg-amber-500/5 border border-amber-500/20 rounded-xl">
              <LockClosedIcon style={{ width: 16, height: 16 }} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-foreground">Login required to continue</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">You'll be asked to sign in before filling in your ad details.</p>
              </div>
            </div>
          )}

          <button
            onClick={() => {
              if (!user) {
                if (typeof window !== 'undefined') {
                  sessionStorage.setItem('pending_ad_type', selectedType ?? '');
                  sessionStorage.setItem('pending_ad_slot', selectedSlot ?? '');
                }
                router.push('/login?redirect=/place-ad&reason=ad');
                return;
              }
              setStep(4);
            }}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            {!user ? (
              <><LockClosedIcon style={{ width: 14, height: 14 }} />Sign In to Continue</>
            ) : (
              <>Continue to Ad Details</>
            )}
          </button>
        </div>
      )}

      {/* ── Step 4: Ad Details Form ── */}
      {step === 4 && (
        <div className="space-y-5">
          {/* Summary bar */}
          <div className="flex items-center gap-2 p-3 bg-card border border-border rounded-xl flex-wrap">
            {selectedTypeData && (
              <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${selectedTypeData.bgColor}`}>
                <selectedTypeData.icon style={{ width: 13, height: 13 }} className={selectedTypeData.color} />
                <span className={`text-[11px] font-semibold ${selectedTypeData.color}`}>{selectedTypeData.name}</span>
              </div>
            )}
            <span className="text-muted-foreground text-xs">→</span>
            {selectedSlotData && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted">
                <selectedSlotData.pageIcon style={{ width: 13, height: 13 }} className="text-muted-foreground" />
                <span className="text-[11px] font-semibold text-foreground">{selectedSlotData.label}</span>
              </div>
            )}
            <span className="text-muted-foreground text-xs">→</span>
            <div className={`px-2.5 py-1.5 rounded-lg ${adModel === 'free_exchange' ? 'bg-purple-500/10' : 'bg-primary/10'}`}>
              <span className={`text-[11px] font-semibold ${adModel === 'free_exchange' ? 'text-purple-400' : 'text-primary'}`}>
                {adModel === 'free_exchange' ? 'Free Exchange' : adModel === 'free' ? 'Free Ad - 24 hours' : 'Paid (USDT)'}
              </span>
            </div>
            <button onClick={() => setStep(1)} className="text-xs text-primary hover:underline ml-auto">Change</button>
          </div>

          {/* Ad Placement Info Panel */}
          {selectedSlotData && selectedTypeData && (
            <AdPlacementInfoPanel slotData={selectedSlotData} adTypeData={selectedTypeData} />
          )}

          {/* Live Ad Preview */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <EyeIcon style={{ width: 14, height: 14 }} className="text-primary" />
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Live Preview</span>
              <span className="text-[10px] text-muted-foreground ml-auto">Updates as you type</span>
            </div>
            <AdPreview
              adType={selectedType}
              headline={form.headline}
              description={form.description}
              ctaText={form.ctaText}
              imagePreview={imagePreview}
              projectName={form.projectName}
              placementLabel={selectedSlotData?.label ?? ''}
            />
          </div>

          {/* Pricing Calculator (paid model only) */}
          {adModel === 'paid' && selectedType && (
            <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CurrencyDollarIcon style={{ width: 15, height: 15 }} className="text-green-400" />
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Pricing Calculator</span>
              </div>
              <div className="grid grid-cols-3 gap-3 items-end">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Daily Rate</p>
                  <p className="text-lg font-bold text-foreground">${getDailyRate()} <span className="text-xs text-muted-foreground font-normal">USDT/day</span></p>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground mb-1 block">Number of Days</label>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={form.durationDays}
                    onChange={(e) => setForm(p => ({ ...p, durationDays: Math.max(1, parseInt(e.target.value) || 1) }))}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-green-500/50 transition-colors"
                  />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Total to Pay</p>
                  <p className="text-xl font-bold text-green-400">{getTotalUsdt()} <span className="text-sm font-semibold">USDT</span></p>
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground bg-background/60 rounded-lg px-3 py-2">
                ${getDailyRate()} × {form.durationDays} days = <span className="font-bold text-green-400">{getTotalUsdt()} USDT</span>
              </div>
              {adminUsdtAddress && (
                <div className="flex items-start gap-2 p-3 bg-background/60 rounded-lg border border-border">
                  <WalletIcon style={{ width: 14, height: 14 }} className="text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-semibold text-foreground mb-0.5">Admin USDT Wallet (send payment after submission)</p>
                    <p className="text-[10px] font-mono text-muted-foreground break-all">{adminUsdtAddress}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Free Exchange Instructions */}
          {adModel === 'free_exchange' && (
            <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CameraIcon style={{ width: 15, height: 15 }} className="text-purple-400" />
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Free Ad Exchange Requirements</span>
              </div>
              <div className="space-y-2 text-xs text-muted-foreground">
                {['Place the BizzBox ad/feature banner on your website', 'Take a screenshot showing the BizzBox ad on your website', 'Upload the screenshot below as proof', 'Admin reviews and assigns your free ad placement (30 days)'].map((s, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full bg-purple-500/20 text-purple-400 text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Project Name *</label>
                <input
                  type="text"
                  placeholder="e.g. StellarBit Exchange"
                  value={form.projectName}
                  onChange={(e) => setForm(p => ({ ...p, projectName: e.target.value }))}
                  className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Category</label>
                <select
                  value={form.projectCategory}
                  onChange={(e) => setForm(p => ({ ...p, projectCategory: e.target.value }))}
                  className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
                >
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Project URL</label>
              <input
                type="url"
                placeholder="https://yourproject.com"
                value={form.projectUrl}
                onChange={(e) => setForm(p => ({ ...p, projectUrl: e.target.value }))}
                className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              />
            </div>

            {adModel === 'free_exchange' && (
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  Your Website URL <span className="text-purple-400">(where BizzBox ad is placed)</span>
                </label>
                <div className="relative">
                  <GlobeAltIcon style={{ width: 15, height: 15 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="url"
                    placeholder="https://yourwebsite.com"
                    value={form.advertiserWebsiteUrl}
                    onChange={(e) => setForm(p => ({ ...p, advertiserWebsiteUrl: e.target.value }))}
                    className="w-full bg-card border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-purple-500/50 transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Ad Image Upload */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
                Ad Image <span className="text-muted-foreground/60 normal-case font-normal">(optional)</span>
              </label>
              <div className="mb-3 p-3 bg-blue-500/8 border border-blue-500/20 rounded-xl">
                <p className="text-xs font-semibold text-blue-400 mb-2 flex items-center gap-1.5">
                  <svg style={{ width: 13, height: 13 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>
                  Image Dimension Guidelines
                </p>
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <div className="bg-background/60 rounded-lg p-2 text-center">
                    <p className="font-bold text-foreground">Banner / Flash</p>
                    <p className="text-primary font-mono mt-0.5">1200 × 300 px</p>
                    <p className="text-muted-foreground mt-0.5">4:1 ratio</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-2 text-center">
                    <p className="font-bold text-foreground">Featured / Post</p>
                    <p className="text-primary font-mono mt-0.5">800 × 600 px</p>
                    <p className="text-muted-foreground mt-0.5">4:3 ratio</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-2 text-center">
                    <p className="font-bold text-foreground">Mini / Side</p>
                    <p className="text-primary font-mono mt-0.5">300 × 200 px</p>
                    <p className="text-muted-foreground mt-0.5">3:2 ratio</p>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Formats: JPG, PNG, WebP · Max file size: <span className="text-foreground font-semibold">2 MB</span>
                </p>
              </div>
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="Ad preview" className="w-full max-h-40 object-cover rounded-xl border border-border" />
                  <button type="button" onClick={removeImage} className="absolute top-2 right-2 p-1.5 rounded-full bg-background/90 text-foreground hover:bg-background transition-colors shadow-sm">
                    <XMarkIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 p-5 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all">
                  <CloudArrowUpIcon style={{ width: 28, height: 28 }} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Click to upload image (JPG, PNG, WebP)</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageChange} />
                </label>
              )}
            </div>

            {/* Screenshot Upload (free exchange only) */}
            {adModel === 'free_exchange' && (
              <div>
                <label className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-2 block">
                  Screenshot Proof * <span className="text-muted-foreground normal-case font-normal">(BizzBox ad on your website)</span>
                </label>
                {screenshotPreview ? (
                  <div className="relative">
                    <img src={screenshotPreview} alt="Screenshot showing BizzBox ad on advertiser website" className="w-full max-h-48 object-cover rounded-xl border border-purple-500/30" />
                    <button type="button" onClick={removeScreenshot} className="absolute top-2 right-2 p-1.5 rounded-full bg-background/90 text-foreground hover:bg-background transition-colors shadow-sm">
                      <XMarkIcon style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center gap-2 p-5 border-2 border-dashed border-purple-500/30 rounded-xl cursor-pointer hover:border-purple-500/50 hover:bg-purple-500/5 transition-all">
                    <CameraIcon style={{ width: 28, height: 28 }} className="text-purple-400/60" />
                    <span className="text-xs text-muted-foreground">Upload screenshot of BizzBox ad on your website</span>
                    <span className="text-[10px] text-muted-foreground">JPG, PNG, WebP · Max 5MB</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleScreenshotChange} />
                  </label>
                )}
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Ad Headline *</label>
              <input
                type="text"
                placeholder="Compelling headline (max 80 chars)"
                maxLength={80}
                value={form.headline}
                onChange={(e) => setForm(p => ({ ...p, headline: e.target.value }))}
                className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              />
              <p className="text-[10px] text-muted-foreground mt-1">{form.headline.length}/80 characters</p>
            </div>

            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Description</label>
              <textarea
                rows={3}
                placeholder="Brief description of your project and offer..."
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">CTA Button Text</label>
                <input
                  type="text"
                  placeholder="Learn More"
                  value={form.ctaText}
                  onChange={(e) => setForm(p => ({ ...p, ctaText: e.target.value }))}
                  className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                />
              </div>
              {adModel === 'free_exchange' && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Notes for Admin</label>
                  <input
                    type="text"
                    placeholder="Any notes about placement..."
                    value={form.freeAdNotes}
                    onChange={(e) => setForm(p => ({ ...p, freeAdNotes: e.target.value }))}
                    className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
            <p className="text-xs text-amber-500 font-semibold mb-1">Transparency Notice</p>
            <p className="text-xs text-muted-foreground">All advertisements on BizzBox are clearly marked as "Sponsored Content". We do not endorse any advertised project. Ads are reviewed before going live.</p>
          </div>

          <div className="flex gap-3">
            {isSlotLocked ? (
              <button onClick={() => setStep(1)} className="px-4 py-2.5 rounded-xl bg-muted text-muted-foreground hover:text-foreground text-sm font-semibold transition-colors">
                Back
              </button>
            ) : (
              <button onClick={() => setStep(3)} className="px-4 py-2.5 rounded-xl bg-muted text-muted-foreground hover:text-foreground text-sm font-semibold transition-colors">
                Back
              </button>
            )}
            {isSlotLocked ? (
              <button
                onClick={() => {
                  if (!form.projectName || !form.headline) {
                    toast.error('Please fill in the required fields (Project Name & Headline)');
                    return;
                  }
                  if (adModel === 'free_exchange' && !screenshotFile) {
                    toast.error('Please upload a screenshot of BizzBox ad on your website');
                    return;
                  }
                  setStep(3);
                }}
                disabled={!form.projectName || !form.headline}
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <EyeIcon style={{ width: 14, height: 14 }} />
                Preview Ad
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting || !form.projectName || !form.headline || (adModel === 'free_exchange' && !screenshotFile)}
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting && <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />}
                {adModel === 'paid' ? `Submit & Pay ${getTotalUsdt()} USDT` : 'Submit Free Ad Request'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
